import { useEffect, useState, useSyncExternalStore } from 'react';
import { useSelector } from 'react-redux';
import type { PayBasis } from '../models/general';

/**
 * Hourly pay visibility flags (docs/hourly-pay-spec.md §7.2): `Account/GetUserInfo` gains
 * `payBasis` on the user and `hourlyPayEnabled`, the company switch. Hourly surfaces show only when
 * BOTH are set, and a client that sees neither field treats the user as Salaried, which keeps the
 * feature dark on older servers.
 *
 * WHY A SMALL MODULE STORE AND NOT REDUX. The user slice's INIT branch copies GetUserInfo fields one
 * by one, and adding a field to it changes the inferred reducer type — which shifts the
 * "... N more ..." elision count inside TypeScript messages that `scripts/tsc-baseline.json` keys on
 * (see the note in userReducer.ts). So the flags are recorded where EVERY GetUserInfo response
 * already passes — `getUserInfoCoalesced` in api/General/account.ts — and read back through a hook.
 * A per-user localStorage copy lets a returning session render the right nav before the boot
 * GetUserInfo lands; the live response always overwrites it.
 *
 * ⚠️ AN AFFORDANCE, NEVER A BOUNDARY. The server decides what `My`/`Queue` return and who may decide.
 */

export interface HourlyPayFlags {
  payBasis: PayBasis;
  hourlyPayEnabled: boolean;
}

interface CachedFlags extends HourlyPayFlags {
  userId: string;
  /** True once a LIVE GetUserInfo response has been recorded this page load (not just the cache). */
  live: boolean;
}

const STORAGE_KEY = 'mowazaf.hourlyPay.v1';

let current: CachedFlags | null = readCache();
const listeners = new Set<() => void>();

function readCache(): CachedFlags | null {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<CachedFlags>;
    if (typeof parsed.userId !== 'string' || !parsed.userId) return null;
    return {
      userId: parsed.userId,
      payBasis: parsed.payBasis === 'Hourly' ? 'Hourly' : 'Salaried',
      hourlyPayEnabled: parsed.hourlyPayEnabled === true,
      live: false,
    };
  } catch {
    return null;
  }
}

function writeCache(value: CachedFlags): void {
  try {
    const { userId, payBasis, hourlyPayEnabled } = value;
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ userId, payBasis, hourlyPayEnabled }));
  } catch {
    /* storage full / disabled — the in-memory copy still serves this session */
  }
}

/** `'Hourly'`, `1` (numeric enum on a non-string serializer) → Hourly; anything else → Salaried. */
function normalisePayBasis(value: unknown): PayBasis {
  if (value === 'Hourly' || value === 1 || value === '1') return 'Hourly';
  return 'Salaried';
}

/**
 * Called with every GetUserInfo payload. Never throws: it sits inside the boot request's promise
 * chain, and a throw there would surface as the boot-error panel.
 */
export function recordHourlyPayFlags(payload: unknown): void {
  try {
    if (!payload || typeof payload !== 'object') return;
    const user = payload as {
      id?: unknown;
      payBasis?: unknown;
      hourlyPayEnabled?: unknown;
      company?: { hourlyPayEnabled?: unknown } | null;
    };
    if (typeof user.id !== 'string' || !user.id) return;
    const next: CachedFlags = {
      userId: user.id,
      payBasis: normalisePayBasis(user.payBasis),
      hourlyPayEnabled: user.hourlyPayEnabled === true || user.company?.hourlyPayEnabled === true,
      live: true,
    };
    if (
      current &&
      current.live &&
      current.userId === next.userId &&
      current.payBasis === next.payBasis &&
      current.hourlyPayEnabled === next.hourlyPayEnabled
    ) {
      return;
    }
    current = next;
    writeCache(next);
    listeners.forEach((listener) => {
      try {
        listener();
      } catch {
        /* one broken subscriber must not starve the others */
      }
    });
  } catch {
    /* see above */
  }
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function snapshot(): CachedFlags | null {
  return current;
}

export interface HourlyPayAccess extends HourlyPayFlags {
  /** False until a GetUserInfo response (or this user's cached copy) has been seen. */
  known: boolean;
  /** True once a live GetUserInfo response confirmed the flags this page load. */
  live: boolean;
  /** The company switch is on. Approver surfaces key off this alone. */
  companyEnabled: boolean;
  /** Hourly employee in an enabled company: My shifts, earnings. */
  showMyShifts: boolean;
}

/** Live flags for the signed-in user. Absent/unknown reads as Salaried + disabled (fail closed). */
export function useHourlyPayAccess(): HourlyPayAccess {
  const userId = useSelector((state: { user?: { userId?: string | null } }) => state.user?.userId ?? null);
  const flags = useSyncExternalStore(subscribe, snapshot, snapshot);
  const mine = flags && userId && flags.userId === userId ? flags : null;
  const payBasis: PayBasis = mine?.payBasis ?? 'Salaried';
  const companyEnabled = mine?.hourlyPayEnabled === true;
  return {
    known: mine !== null,
    live: mine?.live === true,
    payBasis,
    hourlyPayEnabled: companyEnabled,
    companyEnabled,
    showMyShifts: companyEnabled && payBasis === 'Hourly',
  };
}

/** How long a route guard waits for the flags before deciding with "unknown" (= disabled). */
export const HOURLY_FLAGS_MAX_WAIT_MS = 15000;

/** True once the guard should stop waiting: the flags are known, or the wait timed out. */
export function useHourlyPayFlagsSettled(known: boolean): boolean {
  const [timedOut, setTimedOut] = useState(false);
  useEffect(() => {
    if (known) return;
    const timer = setTimeout(() => setTimedOut(true), HOURLY_FLAGS_MAX_WAIT_MS);
    return () => clearTimeout(timer);
  }, [known]);
  return known || timedOut;
}
